/**
 * @license
 * Copyright (c) 2024 lihu.
 * Licensed under Apache License 2.0 https://www.apache.org/licenses/LICENSE-2.0
 */
import { utils, registerOverlay } from "klinecharts";

/**
 * Measure overlay for KlineCharts
 * Creates a measuring tool with crosshairs and detailed measurement display
 */
const measure = {
  name: "measure",
  totalStep: 3,
  needDefaultPointFigure: true,
  needDefaultXAxisFigure: true,
  needDefaultYAxisFigure: true,
  
  styles: {
    backgroundColor: "rgba(22, 119, 255, 0.1)",
    tipBackgroundColor: "rgba(22, 119, 255, 0.95)",
    lineColor: "#1677FF",
    textColor: "#FFFFFF",
    borderColor: "#1677FF",
  },

  /**
   * Creates the visual figures for the measure overlay
   */
  createPointFigures({ chart, coordinates, bounding, overlay, yAxis }) {
    if (coordinates.length <= 1) {
      return [];
    }

    const [point1, point2] = coordinates;
    const styles = this._extractStyles(overlay);
    const measurementData = this._calculateMeasurementData(point1, point2, yAxis);
    const displayTexts = this._generateDisplayTexts(overlay, measurementData);

    return this._buildFigures({
      point1,
      point2,
      styles,
      displayTexts,
      bounding
    });
  },

  /**
   * Extracts and validates styles from overlay with enhanced defaults
   */
  _extractStyles(overlay) {
    const overlayStyles = overlay.styles || {};
    return {
      backgroundColor: overlayStyles.backgroundColor || this.styles.backgroundColor,
      tipBackgroundColor: overlayStyles.tipBackgroundColor || this.styles.tipBackgroundColor,
      lineColor: overlayStyles.lineColor || this.styles.lineColor,
      textColor: overlayStyles.textColor || this.styles.textColor,
      borderColor: overlayStyles.borderColor || this.styles.borderColor,
    };
  },

  /**
   * Calculates comprehensive measurement data in pips
   */
  _calculateMeasurementData(point1, point2, yAxis) {
    const price1 = yAxis.convertFromPixel(point1.y);
    const price2 = yAxis.convertFromPixel(point2.y);
    
    const priceDifference = price2 - price1;
    
    // Calculate pips based on decimal places (most forex pairs use 4 or 5 decimal places)
    const pipMultiplier = this._getPipMultiplier(price1);
    
    // Convert price differences to pips
    const totalPips = Math.abs(priceDifference) * pipMultiplier;
    const pips25 = totalPips * 0.25;
    const pips50 = totalPips * 0.5;
    const pips75 = totalPips * 0.75;
    
    // Calculate percentage changes
    const percentageChange = (priceDifference / Math.abs(price1)) * 100;
    const percentage25 = percentageChange * 0.25;
    const percentage50 = percentageChange * 0.5;
    const percentage75 = percentageChange * 0.75;
    
    return {
      price1,
      price2,
      priceDifference,
      totalPips,
      pips25,
      pips50,
      pips75,
      percentageChange,
      percentage25,
      percentage50,
      percentage75,
      isUpward: priceDifference > 0,
      pipMultiplier
    };
  },

  /**
   * Determines pip multiplier based on price level
   * @private
   */
  _getPipMultiplier(price) {
    // For JPY pairs (price > 50), 1 pip = 0.01
    if (price > 50) {
      return 100;
    }
    // For most other pairs, 1 pip = 0.0001
    return 10000;
  },

  /**
   * Generates display texts with enhanced formatting in pips
   */
  _generateDisplayTexts(overlay, data) {
    const customTexts = overlay.extendData?.texts;
    if (customTexts?.length > 0) {
      return customTexts;
    }

    const direction = data.isUpward ? '↗' : '↘';
    const sign = data.isUpward ? '+' : '-';
    
    return [
      `${direction} 总移动: ${this._formatPips(data.totalPips)} pips`,
      `25%: ${sign}${this._formatPips(data.pips25)} pips (${sign}${this._formatPercentage(Math.abs(data.percentage25))}%)`,
      `50%: ${sign}${this._formatPips(data.pips50)} pips (${sign}${this._formatPercentage(Math.abs(data.percentage50))}%)`,
      `75%: ${sign}${this._formatPips(data.pips75)} pips (${sign}${this._formatPercentage(Math.abs(data.percentage75))}%)`,
      `100%: ${sign}${this._formatPips(data.totalPips)} pips (${sign}${this._formatPercentage(Math.abs(data.percentageChange))}%)`,
      `起始: ${this._formatPrice(data.price1)}`,
      `结束: ${this._formatPrice(data.price2)}`
    ];
  },

  /**
   * Builds all figure elements
   */
  _buildFigures({ point1, point2, styles, displayTexts, bounding }) {
    const isLeftToRight = point1.x < point2.x;
    const isTopToBottom = point1.y < point2.y;
    
    const centerCoordinate = {
      x: Math.round((point1.x + point2.x) / 2),
      y: Math.round((point1.y + point2.y) / 2),
    };

    const figures = [];
    
    // Background with border
    figures.push(this._createBackgroundRect(point1, point2, styles));
    
    // Enhanced crosshair lines
    figures.push(...this._createCrosshairLines(point1, point2, centerCoordinate, styles.lineColor));
    
    // Direction arrows
    figures.push(...this._createDirectionArrows(point1, point2, centerCoordinate, isLeftToRight, isTopToBottom, styles.lineColor));
    
    // Enhanced measurement tooltip
    if (displayTexts.length > 0) {
      figures.push(...this._createEnhancedTooltip(displayTexts, centerCoordinate, point2, bounding, isTopToBottom, styles));
    }

    return figures;
  },

  /**
   * Creates the background rectangle figure
   * @private
   */
  _createBackgroundRect(point1, point2, styles) {
    return {
      type: "polygon",
      attrs: {
        coordinates: [
          point1,
          { x: point2.x, y: point1.y },
          point2,
          { x: point1.x, y: point2.y }
        ],
      },
      styles: {
        color: styles.backgroundColor,
        borderColor: styles.borderColor,
        borderWidth: 1,
        borderStyle: "dashed",
      },
    };
  },

  /**
   * Creates crosshair lines (horizontal and vertical)
   * @private
   */
  _createCrosshairLines(point1, point2, centerCoordinate, lineColor) {
    return [
      // Horizontal line
      {
        type: "line",
        attrs: {
          coordinates: [
            { x: point1.x, y: centerCoordinate.y },
            { x: point2.x, y: centerCoordinate.y },
          ],
        },
        styles: { 
          color: lineColor,
          lineWidth: 2,
          lineDash: [5, 5]
        },
      },
      // Vertical line
      {
        type: "line",
        attrs: {
          coordinates: [
            { x: centerCoordinate.x, y: point1.y },
            { x: centerCoordinate.x, y: point2.y },
          ],
        },
        styles: { 
          color: lineColor,
          lineWidth: 2,
          lineDash: [5, 5]
        },
      },
    ];
  },

  /**
   * Creates direction arrows for measurement lines
   * @private
   */
  _createDirectionArrows(point1, point2, centerCoordinate, isLeftToRight, isTopToBottom, lineColor) {
    const arrows = [];
    const arrowSize = 8;
    const arrowOffset = 5;

    // Horizontal arrow with enhanced styling
    const horizontalArrow = {
      type: "polygon",
      attrs: {
        coordinates: isLeftToRight ? [
          { x: point2.x - arrowSize, y: centerCoordinate.y - arrowOffset },
          { x: point2.x, y: centerCoordinate.y },
          { x: point2.x - arrowSize, y: centerCoordinate.y + arrowOffset },
        ] : [
          { x: point2.x + arrowSize, y: centerCoordinate.y - arrowOffset },
          { x: point2.x, y: centerCoordinate.y },
          { x: point2.x + arrowSize, y: centerCoordinate.y + arrowOffset },
        ],
      },
      styles: { 
        color: lineColor,
        borderColor: lineColor,
        borderWidth: 1
      },
    };

    // Vertical arrow with enhanced styling
    const verticalArrow = {
      type: "polygon",
      attrs: {
        coordinates: isTopToBottom ? [
          { x: centerCoordinate.x - arrowOffset, y: point2.y - arrowSize },
          { x: centerCoordinate.x, y: point2.y },
          { x: centerCoordinate.x + arrowOffset, y: point2.y - arrowSize },
        ] : [
          { x: centerCoordinate.x - arrowOffset, y: point2.y + arrowSize },
          { x: centerCoordinate.x, y: point2.y },
          { x: centerCoordinate.x + arrowOffset, y: point2.y + arrowSize },
        ],
      },
      styles: { 
        color: lineColor,
        borderColor: lineColor,
        borderWidth: 1
      },
    };

    arrows.push(horizontalArrow, verticalArrow);
    return arrows;
  },

  /**
   * Creates enhanced measurement tooltip with texts
   * @private
   */
  _createEnhancedTooltip(displayTexts, centerCoordinate, point2, bounding, isTopToBottom, styles) {
    const tooltip = [];
    const tipGap = 12;
    const textGap = 6;
    const horizontalPadding = 16;
    const verticalPadding = 12;
    const lineHeight = 16;
    const borderRadius = 8;

    // Calculate tooltip dimensions with better text measurement
    let maxWidth = 0;
    displayTexts.forEach(text => {
      const textWidth = utils.calcTextWidth(text, 12);
      maxWidth = Math.max(textWidth, maxWidth);
    });
    
    const tooltipWidth = maxWidth + horizontalPadding * 2;
    const tooltipHeight = displayTexts.length * lineHeight + (displayTexts.length - 1) * textGap + verticalPadding * 2;

    // Smart tooltip positioning
    let tooltipX = centerCoordinate.x - tooltipWidth / 2;
    let tooltipY;
    
    // Adjust horizontal position if tooltip goes beyond bounds
    if (tooltipX < 10) tooltipX = 10;
    if (tooltipX + tooltipWidth > bounding.width - 10) tooltipX = bounding.width - tooltipWidth - 10;

    // Adjust vertical position
    if (isTopToBottom) {
      tooltipY = (point2.y + tipGap + tooltipHeight > bounding.height - 10) 
        ? point2.y - tipGap - tooltipHeight 
        : point2.y + tipGap;
    } else {
      tooltipY = (point2.y - tipGap - tooltipHeight < 10) 
        ? point2.y + tipGap 
        : point2.y - tipGap - tooltipHeight;
    }

    // Enhanced tooltip background with shadow effect
    tooltip.push({
      type: "rect",
      attrs: {
        x: tooltipX,
        y: tooltipY,
        width: tooltipWidth,
        height: tooltipHeight,
      },
      styles: {
        borderRadius: borderRadius,
        color: styles.tipBackgroundColor,
        borderColor: styles.borderColor,
        borderWidth: 1,
        shadowColor: "rgba(0, 0, 0, 0.2)",
        shadowBlur: 4,
        shadowOffsetX: 2,
        shadowOffsetY: 2,
      },
    });

    // Add text lines with improved styling
    let textY = tooltipY + verticalPadding + lineHeight / 2;
    displayTexts.forEach((text, index) => {
      const isHeader = index === 0;
      tooltip.push({
        type: "text",
        attrs: {
          x: tooltipX + tooltipWidth / 2,
          y: textY,
          text: text,
          align: "center",
          baseline: "middle",
        },
        styles: {
          color: styles.textColor,
          fontSize: isHeader ? 13 : 12,
          fontWeight: isHeader ? "bold" : "normal",
          paddingLeft: 0,
          paddingTop: 0,
          paddingRight: 0,
          paddingBottom: 0,
          backgroundColor: "transparent",
        },
      });
      textY += lineHeight + textGap;
    });

    return tooltip;
  },

  /**
   * Formats pips values
   * @private
   */
  _formatPips(pips) {
    return pips.toFixed(1);
  },

  /**
   * Formats price values for forex
   * @private
   */
  _formatPrice(price) {
    // For JPY pairs, show 3 decimal places, for others show 5
    if (price > 50) {
      return price.toFixed(3);
    } else {
      return price.toFixed(5);
    }
  },

  /**
   * Formats numbers with appropriate precision
   * @private
   */
  _formatNumber(value) {
    if (Math.abs(value) >= 1) {
      return value.toFixed(2);
    } else {
      return value.toFixed(4);
    }
  },

  /**
   * Formats percentage values
   * @private
   */
  _formatPercentage(value) {
    return value.toFixed(2);
  }
};

// Register the overlay with KlineCharts
registerOverlay(measure);